/**
 * BOF-76 — reads one sheet of an `.xlsx` workbook in the browser, without a dependency.
 *
 * Deliberately small: it exists to read the charge-point history out of the *cocharge - Charging
 * Station Dashboard* workbook, which is one sheet of plain numbers. It returns each cell's stored
 * value — for a formula, the result the spreadsheet last calculated — and nothing about formatting.
 *
 * An `.xlsx` file is a zip of XML parts. This reads the zip's central directory, inflates only the parts
 * it needs (the workbook, its relationships, the shared strings and the one sheet) with the browser's
 * own `DecompressionStream`, and parses the workbook and sheet with `DOMParser` (the potentially large
 * shared-string table with a linear scan). The inflater is injectable so tests can run under jsdom, which
 * has no `DecompressionStream`.
 */

export type CellValue = string | number | boolean | null;

export type InflateRaw = (data: Uint8Array) => Promise<Uint8Array>;

export class XlsxReadError extends Error {}

const browserInflateRaw: InflateRaw = async (data) => {
  const Decompression = (window as any).DecompressionStream;
  if (!Decompression) throw new XlsxReadError("This browser cannot read .xlsx files; export the sheet as CSV instead.");
  const stream = (new Blob([data]) as any).stream().pipeThrough(new Decompression("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
};

interface ZipEntry {
  method: number;
  compressedSize: number;
  localHeaderOffset: number;
}

const EOCD_SIGNATURE = 0x06054b50;
const CENTRAL_SIGNATURE = 0x02014b50;
const LOCAL_SIGNATURE = 0x04034b50;

const readCentralDirectory = (bytes: Uint8Array): Map<string, ZipEntry> => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  // The end-of-central-directory record sits in the last 22 bytes plus an optional comment of up to 64 KiB.
  let eocd = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 22 - 0xffff); i--) {
    if (view.getUint32(i, true) === EOCD_SIGNATURE) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new XlsxReadError("Not an .xlsx file: no zip directory found.");

  const count = view.getUint16(eocd + 10, true);
  let offset = view.getUint32(eocd + 16, true);
  const decoder = new TextDecoder("utf-8");
  const entries = new Map<string, ZipEntry>();
  for (let i = 0; i < count; i++) {
    if (view.getUint32(offset, true) !== CENTRAL_SIGNATURE) throw new XlsxReadError("Corrupt .xlsx file.");
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const name = decoder.decode(bytes.subarray(offset + 46, offset + 46 + nameLength));
    entries.set(name, {
      method: view.getUint16(offset + 10, true),
      compressedSize: view.getUint32(offset + 20, true),
      localHeaderOffset: view.getUint32(offset + 42, true),
    });
    offset += 46 + nameLength + extraLength + commentLength;
  }
  return entries;
};

const readEntry = async (
  bytes: Uint8Array,
  entry: ZipEntry,
  inflateRaw: InflateRaw,
): Promise<string> => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const at = entry.localHeaderOffset;
  if (view.getUint32(at, true) !== LOCAL_SIGNATURE) throw new XlsxReadError("Corrupt .xlsx file.");
  const start = at + 30 + view.getUint16(at + 26, true) + view.getUint16(at + 28, true);
  const raw = bytes.subarray(start, start + entry.compressedSize);
  let data: Uint8Array;
  if (entry.method === 0) data = raw;
  else if (entry.method === 8) data = await inflateRaw(raw);
  else throw new XlsxReadError(`Unsupported .xlsx compression (method ${entry.method}).`);
  return new TextDecoder("utf-8").decode(data);
};

const parseXml = (text: string): Document => {
  const doc = new DOMParser().parseFromString(text, "application/xml");
  if (doc.getElementsByTagName("parsererror").length) throw new XlsxReadError("Corrupt .xlsx file: unreadable XML.");
  return doc;
};

/** "D12" → 3. */
const columnIndex = (ref: string): number => {
  let index = 0;
  for (const ch of ref) {
    const code = ch.charCodeAt(0);
    if (code < 65 || code > 90) break;
    index = index * 26 + (code - 64);
  }
  return index - 1;
};

const XML_ENTITIES: Record<string, string> = { lt: "<", gt: ">", amp: "&", quot: '"', apos: "'" };

const decodeXml = (s: string): string =>
  s.replace(/&(#x[0-9a-fA-F]+|#[0-9]+|[a-z]+);/g, (whole, code: string) => {
    if (code[0] === "#") {
      const n = code[1] === "x" ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
      return Number.isFinite(n) ? String.fromCodePoint(n) : whole;
    }
    return XML_ENTITIES[code] ?? whole;
  });

/**
 * The shared-string table, read with a linear scan rather than a DOM. A workbook with pivot tables
 * carries megabytes of strings here (the reference workbook: 4 MB), and only a handful are needed.
 * Each `<si>` is the concatenation of its `<t>` runs; phonetic runs (`<rPh>`) are not part of the text.
 */
const sharedStringsOf = (xml: string): string[] => {
  const strings: string[] = [];
  const item = /<si>([\s\S]*?)<\/si>|<si\/>/g;
  const run = /<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/g;
  let m: RegExpExecArray | null;
  while ((m = item.exec(xml))) {
    const body = (m[1] ?? "").replace(/<rPh[\s\S]*?<\/rPh>/g, "");
    let text = "";
    let t: RegExpExecArray | null;
    run.lastIndex = 0;
    while ((t = run.exec(body))) text += t[1];
    strings.push(decodeXml(text));
  }
  return strings;
};

const textOf = (el: Element): string =>
  Array.from(el.getElementsByTagName("t"))
    .map((t) => t.textContent ?? "")
    .join("");

/** Parses one worksheet part into zero-based rows: `rows[1][3]` is cell D2. Missing cells are `null`. */
const rowsOf = (sheetXml: string, shared: string[]): CellValue[][] => {
  const rows: CellValue[][] = [];
  for (const row of Array.from(parseXml(sheetXml).getElementsByTagName("row"))) {
    const r = Number(row.getAttribute("r")) - 1;
    if (!(r >= 0)) continue;
    const cells: CellValue[] = [];
    for (const c of Array.from(row.getElementsByTagName("c"))) {
      const col = columnIndex(c.getAttribute("r") ?? "");
      if (col < 0) continue;
      const type = c.getAttribute("t");
      const v = c.getElementsByTagName("v")[0]?.textContent ?? null;
      let value: CellValue = null;
      if (type === "s") value = v == null ? null : shared[Number(v)] ?? null;
      else if (type === "inlineStr") value = textOf(c);
      else if (type === "str") value = v;
      else if (type === "b") value = v === "1";
      else if (type === "e") value = null;
      else value = v == null || v === "" ? null : Number(v);
      cells[col] = value;
    }
    rows[r] = Array.from(cells, (x) => (x === undefined ? null : x));
  }
  return Array.from(rows, (x) => x ?? []);
};

/**
 * The rows of every sheet whose name `pick` accepts, keyed by sheet name — one pass over the zip, the
 * shared strings read once. `pick` receives every sheet name in workbook order.
 */
export const readXlsxSheets = async (
  file: ArrayBuffer | Uint8Array,
  pick: (sheetNames: string[]) => string[],
  inflateRaw: InflateRaw = browserInflateRaw,
): Promise<Record<string, CellValue[][]>> => {
  const bytes = file instanceof Uint8Array ? file : new Uint8Array(file);
  const entries = readCentralDirectory(bytes);
  const part = async (name: string) => {
    const entry = entries.get(name);
    return entry ? readEntry(bytes, entry, inflateRaw) : null;
  };

  const workbook = await part("xl/workbook.xml");
  const rels = await part("xl/_rels/workbook.xml.rels");
  if (!workbook || !rels) throw new XlsxReadError("Not an .xlsx workbook.");

  const sheets = Array.from(parseXml(workbook).getElementsByTagName("sheet"));
  const wanted = new Set(pick(sheets.map((s) => s.getAttribute("name") ?? "")));
  const relationships = Array.from(parseXml(rels).getElementsByTagName("Relationship"));

  const sharedXml = wanted.size ? await part("xl/sharedStrings.xml") : null;
  const shared = sharedXml ? sharedStringsOf(sharedXml) : [];

  const result: Record<string, CellValue[][]> = {};
  for (const sheet of sheets) {
    const name = sheet.getAttribute("name") ?? "";
    if (!wanted.has(name)) continue;
    const target = relationships.find((r) => r.getAttribute("Id") === sheet.getAttribute("r:id"))?.getAttribute("Target");
    if (!target) throw new XlsxReadError(`Cannot locate sheet "${name}" in the workbook.`);
    const sheetXml = await part(target.startsWith("/") ? target.slice(1) : `xl/${target}`);
    if (!sheetXml) throw new XlsxReadError(`Sheet "${name}" is missing from the file.`);
    result[name] = rowsOf(sheetXml, shared);
  }
  return result;
};

/**
 * The rows of `sheetName`, zero-based: `rows[1][3]` is cell D2. Missing rows and cells are `null`.
 * Throws {@link XlsxReadError} with a readable message when the file or the sheet cannot be read.
 */
export const readXlsxSheet = async (
  file: ArrayBuffer | Uint8Array,
  sheetName: string,
  inflateRaw: InflateRaw = browserInflateRaw,
): Promise<CellValue[][]> => {
  const sheets = await readXlsxSheets(file, (names) => names.filter((n) => n === sheetName), inflateRaw);
  const rows = sheets[sheetName];
  if (!rows) throw new XlsxReadError(`The workbook has no sheet named "${sheetName}".`);
  return rows;
};
